import { useCallback, useMemo, useEffect, useState, memo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Box, Download, Maximize2, Code, Eye, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import html2canvas from 'html2canvas';

// Cloud Provider Color Palettes (Official palettes)
const CLOUD_COLORS = {
  AWS: {
    compute: { bg: '#ED7100', border: '#C45500' },
    database: { bg: '#3B48CC', border: '#2D3A9E' },
    storage: { bg: '#3F8624', border: '#2E6419' },
    network: { bg: '#8C4FFF', border: '#6B3ACC' },
    security: { bg: '#DD344C', border: '#B52A3E' },
    management: { bg: '#E7157B', border: '#B8115F' },
    integration: { bg: '#E7157B', border: '#B8115F' },
    devops: { bg: '#3B48CC', border: '#2D3A9E' },
    analytics: { bg: '#8C4FFF', border: '#6B3ACC' },
    external: { bg: '#232F3E', border: '#1A242F' },
  },
  Azure: {
    compute: { bg: '#0078D4', border: '#005A9E' },
    database: { bg: '#0078D4', border: '#005A9E' },
    storage: { bg: '#0078D4', border: '#005A9E' },
    network: { bg: '#773ADC', border: '#5C2D91' },
    security: { bg: '#DD5900', border: '#B84700' },
    management: { bg: '#0078D4', border: '#005A9E' },
    integration: { bg: '#773ADC', border: '#5C2D91' },
    devops: { bg: '#0078D4', border: '#005A9E' },
    analytics: { bg: '#0078D4', border: '#005A9E' },
    external: { bg: '#3C3C41', border: '#2D2D30' },
  },
  GCP: {
    compute: { bg: '#4285F4', border: '#3367D6' },
    database: { bg: '#4285F4', border: '#3367D6' },
    storage: { bg: '#4285F4', border: '#3367D6' },
    network: { bg: '#4285F4', border: '#3367D6' },
    security: { bg: '#EA4335', border: '#C5221F' },
    management: { bg: '#34A853', border: '#2D8F47' },
    integration: { bg: '#FBBC04', border: '#E8A100' },
    devops: { bg: '#4285F4', border: '#3367D6' },
    analytics: { bg: '#4285F4', border: '#3367D6' },
    external: { bg: '#5F6368', border: '#4A4E52' },
  },
};

// Get colors based on cloud platform
const getCloudColors = (platform = 'AWS') => {
  return CLOUD_COLORS[platform] || CLOUD_COLORS.AWS;
};

// Service name mappings across cloud providers
const SERVICE_MAPPINGS = {
  // Compute
  ec2: { AWS: 'EC2', Azure: 'Virtual Machines', GCP: 'Compute Engine' },
  ecs: { AWS: 'ECS', Azure: 'Container Instances', GCP: 'Cloud Run' },
  eks: { AWS: 'EKS', Azure: 'AKS', GCP: 'GKE' },
  lambda: { AWS: 'Lambda', Azure: 'Functions', GCP: 'Cloud Functions' },
  fargate: { AWS: 'Fargate', Azure: 'Container Apps', GCP: 'Cloud Run' },
  
  // Database
  rds: { AWS: 'RDS', Azure: 'Azure SQL', GCP: 'Cloud SQL' },
  aurora: { AWS: 'Aurora', Azure: 'Azure SQL', GCP: 'Cloud SQL' },
  dynamodb: { AWS: 'DynamoDB', Azure: 'Cosmos DB', GCP: 'Firestore' },
  elasticache: { AWS: 'ElastiCache', Azure: 'Cache for Redis', GCP: 'Memorystore' },
  
  // Storage
  s3: { AWS: 'S3', Azure: 'Blob Storage', GCP: 'Cloud Storage' },
  ebs: { AWS: 'EBS', Azure: 'Managed Disks', GCP: 'Persistent Disk' },
  
  // Network
  elb: { AWS: 'ALB/NLB', Azure: 'Load Balancer', GCP: 'Cloud Load Balancing' },
  cloudfront: { AWS: 'CloudFront', Azure: 'CDN', GCP: 'Cloud CDN' },
  route53: { AWS: 'Route 53', Azure: 'DNS', GCP: 'Cloud DNS' },
  vpc: { AWS: 'VPC', Azure: 'VNet', GCP: 'VPC Network' },
  apigateway: { AWS: 'API Gateway', Azure: 'API Management', GCP: 'API Gateway' },
  nat: { AWS: 'NAT Gateway', Azure: 'NAT Gateway', GCP: 'Cloud NAT' },
  
  // Security
  waf: { AWS: 'WAF', Azure: 'WAF', GCP: 'Cloud Armor' },
  iam: { AWS: 'IAM', Azure: 'Azure AD', GCP: 'Cloud IAM' },
  kms: { AWS: 'KMS', Azure: 'Key Vault', GCP: 'Cloud KMS' },
  secretsmanager: { AWS: 'Secrets Manager', Azure: 'Key Vault', GCP: 'Secret Manager' },
  shield: { AWS: 'Shield', Azure: 'DDoS Protection', GCP: 'Cloud Armor' },
  
  // Monitoring
  cloudwatch: { AWS: 'CloudWatch', Azure: 'Monitor', GCP: 'Cloud Monitoring' },
  xray: { AWS: 'X-Ray', Azure: 'App Insights', GCP: 'Cloud Trace' },
  
  // Integration
  sns: { AWS: 'SNS', Azure: 'Service Bus', GCP: 'Pub/Sub' },
  sqs: { AWS: 'SQS', Azure: 'Queue Storage', GCP: 'Cloud Tasks' },
  kinesis: { AWS: 'Kinesis', Azure: 'Event Hubs', GCP: 'Dataflow' },
  eventbridge: { AWS: 'EventBridge', Azure: 'Event Grid', GCP: 'Eventarc' },
  
  // DevOps
  codepipeline: { AWS: 'CodePipeline', Azure: 'Azure DevOps', GCP: 'Cloud Build' },
  ecr: { AWS: 'ECR', Azure: 'Container Registry', GCP: 'Artifact Registry' },
  
  // Analytics
  athena: { AWS: 'Athena', Azure: 'Synapse', GCP: 'BigQuery' },
  redshift: { AWS: 'Redshift', Azure: 'Synapse', GCP: 'BigQuery' },
  emr: { AWS: 'EMR', Azure: 'HDInsight', GCP: 'Dataproc' },
  glue: { AWS: 'Glue', Azure: 'Data Factory', GCP: 'Dataflow' },
};

// Get translated service name for the platform
const getServiceName = (serviceType, platform = 'AWS') => {
  const normalized = serviceType?.toLowerCase().replace(/[\s-_]/g, '') || 'ec2';
  const mapping = SERVICE_MAPPINGS[normalized];
  return mapping ? mapping[platform] || mapping.AWS : serviceType;
};

// Professional Cloud Service Icons as inline SVGs
const ServiceIcons = {
  // Users/External
  users: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M8 44c0-8.8 7.2-16 16-16s16 7.2 16 16" stroke="currentColor" stroke-width="3"/><circle cx="38" cy="16" r="5" fill="currentColor" opacity="0.6"/><path d="M38 24c5 0 9 4 9 9" stroke="currentColor" stroke-width="2" opacity="0.6"/></svg>`,
  browser: `<svg viewBox="0 0 48 48" fill="none"><rect x="4" y="8" width="40" height="32" rx="3" stroke="currentColor" stroke-width="2"/><line x1="4" y1="16" x2="44" y2="16" stroke="currentColor" stroke-width="2"/><circle cx="10" cy="12" r="2" fill="currentColor"/><circle cx="16" cy="12" r="2" fill="currentColor"/><circle cx="22" cy="12" r="2" fill="currentColor"/></svg>`,
  mobile: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="4" width="24" height="40" rx="3" stroke="currentColor" stroke-width="2"/><line x1="12" y1="12" x2="36" y2="12" stroke="currentColor" stroke-width="1.5"/><line x1="12" y1="36" x2="36" y2="36" stroke="currentColor" stroke-width="1.5"/><circle cx="24" cy="40" r="2" fill="currentColor"/></svg>`,
  
  // Network
  route53: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="16" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 8v6M24 34v6M8 24h6M34 24h6" stroke="currentColor" stroke-width="2"/></svg>`,
  cloudfront: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 10v-4M38 24h4M24 38v4M10 24h-4M35 13l3-3M35 35l3 3M13 35l-3 3M13 13l-3-3" stroke="currentColor" stroke-width="1.5"/></svg>`,
  cdn: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 10v-4M38 24h4M24 38v4M10 24h-4M35 13l3-3M35 35l3 3M13 35l-3 3M13 13l-3-3" stroke="currentColor" stroke-width="1.5"/></svg>`,
  elb: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="12" r="6" fill="currentColor"/><circle cx="12" cy="36" r="5" fill="currentColor"/><circle cx="24" cy="36" r="5" fill="currentColor"/><circle cx="36" cy="36" r="5" fill="currentColor"/><path d="M24 18L12 31M24 18v13M24 18l12 13" stroke="currentColor" stroke-width="2"/></svg>`,
  loadbalancer: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="12" r="6" fill="currentColor"/><circle cx="12" cy="36" r="5" fill="currentColor"/><circle cx="24" cy="36" r="5" fill="currentColor"/><circle cx="36" cy="36" r="5" fill="currentColor"/><path d="M24 18L12 31M24 18v13M24 18l12 13" stroke="currentColor" stroke-width="2"/></svg>`,
  vpc: `<svg viewBox="0 0 48 48" fill="none"><rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" stroke-width="2" stroke-dasharray="4 2"/><rect x="12" y="12" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="26" y="12" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="12" y="26" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="26" y="26" width="10" height="10" fill="currentColor" opacity="0.3"/></svg>`,
  vnet: `<svg viewBox="0 0 48 48" fill="none"><rect x="6" y="6" width="36" height="36" rx="4" stroke="currentColor" stroke-width="2" stroke-dasharray="4 2"/><rect x="12" y="12" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="26" y="12" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="12" y="26" width="10" height="10" fill="currentColor" opacity="0.3"/><rect x="26" y="26" width="10" height="10" fill="currentColor" opacity="0.3"/></svg>`,
  apigateway: `<svg viewBox="0 0 48 48" fill="none"><path d="M8 24h6l4-8 4 16 4-16 4 8h10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8" cy="24" r="3" fill="currentColor"/><circle cx="40" cy="24" r="3" fill="currentColor"/></svg>`,
  nat: `<svg viewBox="0 0 48 48" fill="none"><rect x="8" y="16" width="32" height="16" rx="2" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/><path d="M24 8v8M24 32v8M16 24h-8M40 24h-8" stroke="currentColor" stroke-width="2"/><path d="M20 12l4-4 4 4M20 36l4 4 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  
  // Compute - AWS
  ec2: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="10" width="28" height="28" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="16" width="16" height="16" fill="currentColor"/><rect x="6" y="18" width="4" height="4" fill="currentColor"/><rect x="6" y="26" width="4" height="4" fill="currentColor"/><rect x="38" y="18" width="4" height="4" fill="currentColor"/><rect x="38" y="26" width="4" height="4" fill="currentColor"/></svg>`,
  ecs: `<svg viewBox="0 0 48 48" fill="none"><rect x="8" y="14" width="14" height="10" rx="2" fill="currentColor"/><rect x="26" y="14" width="14" height="10" rx="2" fill="currentColor"/><rect x="8" y="28" width="14" height="10" rx="2" fill="currentColor"/><rect x="26" y="28" width="14" height="10" rx="2" fill="currentColor"/><path d="M22 19h4M22 33h4" stroke="currentColor" stroke-width="2"/></svg>`,
  lambda: `<svg viewBox="0 0 48 48" fill="none"><path d="M12 40L24 8l12 32" stroke="currentColor" stroke-width="3" fill="none"/><path d="M16 28h16" stroke="currentColor" stroke-width="2"/></svg>`,
  fargate: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  eks: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/></svg>`,
  
  // Compute - Azure
  virtualmachines: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="10" width="28" height="28" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="16" width="16" height="16" fill="currentColor"/><rect x="6" y="18" width="4" height="4" fill="currentColor"/><rect x="6" y="26" width="4" height="4" fill="currentColor"/><rect x="38" y="18" width="4" height="4" fill="currentColor"/><rect x="38" y="26" width="4" height="4" fill="currentColor"/></svg>`,
  functions: `<svg viewBox="0 0 48 48" fill="none"><path d="M12 40L24 8l12 32" stroke="currentColor" stroke-width="3" fill="none"/><path d="M16 28h16" stroke="currentColor" stroke-width="2"/></svg>`,
  aks: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/></svg>`,
  containerapps: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  
  // Compute - GCP
  computeengine: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="10" width="28" height="28" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="16" width="16" height="16" fill="currentColor"/><rect x="6" y="18" width="4" height="4" fill="currentColor"/><rect x="6" y="26" width="4" height="4" fill="currentColor"/><rect x="38" y="18" width="4" height="4" fill="currentColor"/><rect x="38" y="26" width="4" height="4" fill="currentColor"/></svg>`,
  cloudrun: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  cloudfunctions: `<svg viewBox="0 0 48 48" fill="none"><path d="M12 40L24 8l12 32" stroke="currentColor" stroke-width="3" fill="none"/><path d="M16 28h16" stroke="currentColor" stroke-width="2"/></svg>`,
  gke: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/></svg>`,
  
  // Database
  rds: `<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="14" rx="14" ry="6" fill="currentColor"/><path d="M10 14v20c0 3.3 6.3 6 14 6s14-2.7 14-6V14" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="14" ry="6" fill="currentColor" opacity="0.3"/><ellipse cx="24" cy="34" rx="14" ry="6" fill="currentColor" opacity="0.3"/></svg>`,
  aurora: `<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="12" rx="12" ry="5" fill="currentColor"/><path d="M12 12v24c0 2.8 5.4 5 12 5s12-2.2 12-5V12" stroke="currentColor" stroke-width="2"/><path d="M24 22l-8 8h16l-8-8z" fill="currentColor"/></svg>`,
  dynamodb: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8L10 16v16l14 8 14-8V16L24 8z" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="8" ry="4" fill="currentColor"/></svg>`,
  elasticache: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" fill="currentColor"/><path d="M16 24h4M24 24h4M32 24h4" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M14 12l4 4M34 12l-4 4M14 36l4-4M34 36l-4-4" stroke="currentColor" stroke-width="2"/></svg>`,
  // Azure DB
  azuresql: `<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="14" rx="14" ry="6" fill="currentColor"/><path d="M10 14v20c0 3.3 6.3 6 14 6s14-2.7 14-6V14" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="14" ry="6" fill="currentColor" opacity="0.3"/><ellipse cx="24" cy="34" rx="14" ry="6" fill="currentColor" opacity="0.3"/></svg>`,
  cosmosdb: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="14" ry="6" stroke="currentColor" stroke-width="1.5"/><ellipse cx="24" cy="24" rx="6" ry="14" stroke="currentColor" stroke-width="1.5"/></svg>`,
  cacheforredis: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" fill="currentColor"/><path d="M16 24h4M24 24h4M32 24h4" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M14 12l4 4M34 12l-4 4M14 36l4-4M34 36l-4-4" stroke="currentColor" stroke-width="2"/></svg>`,
  // GCP DB
  cloudsql: `<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="14" rx="14" ry="6" fill="currentColor"/><path d="M10 14v20c0 3.3 6.3 6 14 6s14-2.7 14-6V14" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="14" ry="6" fill="currentColor" opacity="0.3"/><ellipse cx="24" cy="34" rx="14" ry="6" fill="currentColor" opacity="0.3"/></svg>`,
  firestore: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8L10 16v16l14 8 14-8V16L24 8z" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="8" ry="4" fill="currentColor"/></svg>`,
  memorystore: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" fill="currentColor"/><path d="M16 24h4M24 24h4M32 24h4" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M14 12l4 4M34 12l-4 4M14 36l4-4M34 36l-4-4" stroke="currentColor" stroke-width="2"/></svg>`,
  
  // Storage
  s3: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8L8 16v16l16 8 16-8V16L24 8z" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="8" ry="4" fill="currentColor"/><path d="M16 24v8l8 4 8-4v-8" stroke="currentColor" stroke-width="1.5"/></svg>`,
  ebs: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="2" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  blobstorage: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8L8 16v16l16 8 16-8V16L24 8z" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="8" ry="4" fill="currentColor"/><path d="M16 24v8l8 4 8-4v-8" stroke="currentColor" stroke-width="1.5"/></svg>`,
  manageddisks: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="2" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  cloudstorage: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8L8 16v16l16 8 16-8V16L24 8z" stroke="currentColor" stroke-width="2"/><ellipse cx="24" cy="24" rx="8" ry="4" fill="currentColor"/><path d="M16 24v8l8 4 8-4v-8" stroke="currentColor" stroke-width="1.5"/></svg>`,
  persistentdisk: `<svg viewBox="0 0 48 48" fill="none"><rect x="12" y="8" width="24" height="32" rx="2" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/><path d="M18 16h12M18 24h12M18 32h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  
  // Security
  waf: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 6L8 14v14c0 8 6.4 15.4 16 18 9.6-2.6 16-10 16-18V14L24 6z" stroke="currentColor" stroke-width="2"/><path d="M20 24l4 4 8-8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  cloudarmor: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 6L8 14v14c0 8 6.4 15.4 16 18 9.6-2.6 16-10 16-18V14L24 6z" stroke="currentColor" stroke-width="2"/><path d="M20 24l4 4 8-8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  iam: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 40c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
  azuread: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 40c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
  cloudiam: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 40c0-6.6 5.4-12 12-12s12 5.4 12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
  kms: `<svg viewBox="0 0 48 48" fill="none"><circle cx="20" cy="24" r="10" stroke="currentColor" stroke-width="2"/><path d="M28 24h12M36 20v8" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
  keyvault: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="20" width="20" height="18" rx="2" fill="currentColor"/><path d="M24 10a6 6 0 00-6 6v4h12v-4a6 6 0 00-6-6z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="29" r="2" fill="white"/></svg>`,
  cloudkms: `<svg viewBox="0 0 48 48" fill="none"><circle cx="20" cy="24" r="10" stroke="currentColor" stroke-width="2"/><path d="M28 24h12M36 20v8" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>`,
  secretsmanager: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="20" width="20" height="18" rx="2" fill="currentColor"/><path d="M24 10a6 6 0 00-6 6v4h12v-4a6 6 0 00-6-6z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="29" r="2" fill="white"/></svg>`,
  secretmanager: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="20" width="20" height="18" rx="2" fill="currentColor"/><path d="M24 10a6 6 0 00-6 6v4h12v-4a6 6 0 00-6-6z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="29" r="2" fill="white"/></svg>`,
  shield: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 6L8 14v14c0 8 6.4 15.4 16 18 9.6-2.6 16-10 16-18V14L24 6z" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/></svg>`,
  ddosprotection: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 6L8 14v14c0 8 6.4 15.4 16 18 9.6-2.6 16-10 16-18V14L24 6z" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="2"/></svg>`,
  
  // Monitoring
  cloudwatch: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M24 14v10l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>`,
  monitor: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M24 14v10l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>`,
  cloudmonitoring: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M24 14v10l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>`,
  xray: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M14 24h20M24 14v20" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/></svg>`,
  appinsights: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M14 24h20M24 14v20" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/></svg>`,
  cloudtrace: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><path d="M14 24h20M24 14v20" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/></svg>`,
  
  // Integration
  sns: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 36l6-10M36 36l-6-10M24 24v12" stroke="currentColor" stroke-width="2.5"/><circle cx="12" cy="36" r="4" fill="currentColor"/><circle cx="36" cy="36" r="4" fill="currentColor"/><circle cx="24" cy="36" r="4" fill="currentColor"/></svg>`,
  servicebus: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 36l6-10M36 36l-6-10M24 24v12" stroke="currentColor" stroke-width="2.5"/><circle cx="12" cy="36" r="4" fill="currentColor"/><circle cx="36" cy="36" r="4" fill="currentColor"/><circle cx="24" cy="36" r="4" fill="currentColor"/></svg>`,
  pubsub: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="16" r="8" fill="currentColor"/><path d="M12 36l6-10M36 36l-6-10M24 24v12" stroke="currentColor" stroke-width="2.5"/><circle cx="12" cy="36" r="4" fill="currentColor"/><circle cx="36" cy="36" r="4" fill="currentColor"/><circle cx="24" cy="36" r="4" fill="currentColor"/></svg>`,
  sqs: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" stroke="currentColor" stroke-width="2"/><path d="M14 24h6M24 24h6M34 24h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M40 24l4-4v8l-4-4z" fill="currentColor"/></svg>`,
  queuestorage: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" stroke="currentColor" stroke-width="2"/><path d="M14 24h6M24 24h6M34 24h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M40 24l4-4v8l-4-4z" fill="currentColor"/></svg>`,
  cloudtasks: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="16" width="28" height="16" rx="2" stroke="currentColor" stroke-width="2"/><path d="M14 24h6M24 24h6M34 24h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M40 24l4-4v8l-4-4z" fill="currentColor"/></svg>`,
  kinesis: `<svg viewBox="0 0 48 48" fill="none"><path d="M8 14l8 10-8 10M18 14l8 10-8 10M28 14l8 10-8 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  eventhubs: `<svg viewBox="0 0 48 48" fill="none"><path d="M8 14l8 10-8 10M18 14l8 10-8 10M28 14l8 10-8 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  dataflow: `<svg viewBox="0 0 48 48" fill="none"><path d="M8 14l8 10-8 10M18 14l8 10-8 10M28 14l8 10-8 10" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  eventbridge: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="14" width="20" height="20" rx="2" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><path d="M24 8v6M24 34v6M8 24h6M34 24h6" stroke="currentColor" stroke-width="2"/></svg>`,
  eventgrid: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="14" width="20" height="20" rx="2" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><path d="M24 8v6M24 34v6M8 24h6M34 24h6" stroke="currentColor" stroke-width="2"/></svg>`,
  eventarc: `<svg viewBox="0 0 48 48" fill="none"><rect x="14" y="14" width="20" height="20" rx="2" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><path d="M24 8v6M24 34v6M8 24h6M34 24h6" stroke="currentColor" stroke-width="2"/></svg>`,
  
  // DevOps
  codepipeline: `<svg viewBox="0 0 48 48" fill="none"><rect x="6" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="19" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="32" y="18" width="10" height="12" rx="2" fill="currentColor"/><path d="M16 24h3M29 24h3" stroke="currentColor" stroke-width="2"/></svg>`,
  azuredevops: `<svg viewBox="0 0 48 48" fill="none"><rect x="6" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="19" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="32" y="18" width="10" height="12" rx="2" fill="currentColor"/><path d="M16 24h3M29 24h3" stroke="currentColor" stroke-width="2"/></svg>`,
  cloudbuild: `<svg viewBox="0 0 48 48" fill="none"><rect x="6" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="19" y="18" width="10" height="12" rx="2" fill="currentColor"/><rect x="32" y="18" width="10" height="12" rx="2" fill="currentColor"/><path d="M16 24h3M29 24h3" stroke="currentColor" stroke-width="2"/></svg>`,
  ecr: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="14" width="28" height="20" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="20" width="6" height="8" fill="currentColor"/><rect x="26" y="20" width="6" height="8" fill="currentColor"/></svg>`,
  containerregistry: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="14" width="28" height="20" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="20" width="6" height="8" fill="currentColor"/><rect x="26" y="20" width="6" height="8" fill="currentColor"/></svg>`,
  artifactregistry: `<svg viewBox="0 0 48 48" fill="none"><rect x="10" y="14" width="28" height="20" rx="2" stroke="currentColor" stroke-width="2"/><rect x="16" y="20" width="6" height="8" fill="currentColor"/><rect x="26" y="20" width="6" height="8" fill="currentColor"/></svg>`,
  
  // Analytics
  athena: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 18v-6M30 21l5-3M30 27l5 3M24 30v6M18 27l-5 3M18 21l-5-3" stroke="currentColor" stroke-width="1.5"/></svg>`,
  synapse: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 18v-6M30 21l5-3M30 27l5 3M24 30v6M18 27l-5 3M18 21l-5-3" stroke="currentColor" stroke-width="1.5"/></svg>`,
  bigquery: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8l14 8v16l-14 8-14-8V16l14-8z" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="6" fill="currentColor"/><path d="M24 18v-6M30 21l5-3M30 27l5 3M24 30v6M18 27l-5 3M18 21l-5-3" stroke="currentColor" stroke-width="1.5"/></svg>`,
  redshift: `<svg viewBox="0 0 48 48" fill="none"><ellipse cx="24" cy="14" rx="14" ry="6" fill="currentColor"/><path d="M10 14v20c0 3.3 6.3 6 14 6s14-2.7 14-6V14" stroke="currentColor" stroke-width="2"/><path d="M10 24c0 3.3 6.3 6 14 6s14-2.7 14-6" stroke="currentColor" stroke-width="1.5"/><path d="M10 34c0 3.3 6.3 6 14 6s14-2.7 14-6" stroke="currentColor" stroke-width="1.5"/></svg>`,
  emr: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><circle cx="24" cy="12" r="3" fill="currentColor"/><circle cx="35" cy="30" r="3" fill="currentColor"/><circle cx="13" cy="30" r="3" fill="currentColor"/></svg>`,
  hdinsight: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><circle cx="24" cy="12" r="3" fill="currentColor"/><circle cx="35" cy="30" r="3" fill="currentColor"/><circle cx="13" cy="30" r="3" fill="currentColor"/></svg>`,
  dataproc: `<svg viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="14" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="4" fill="currentColor"/><circle cx="24" cy="12" r="3" fill="currentColor"/><circle cx="35" cy="30" r="3" fill="currentColor"/><circle cx="13" cy="30" r="3" fill="currentColor"/></svg>`,
  quicksight: `<svg viewBox="0 0 48 48" fill="none"><rect x="8" y="12" width="32" height="24" rx="2" stroke="currentColor" stroke-width="2"/><path d="M14 28l6-8 6 4 8-10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  glue: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8v8M24 32v8M8 24h8M32 24h8" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="8" fill="currentColor"/><circle cx="24" cy="8" r="3" fill="currentColor"/><circle cx="24" cy="40" r="3" fill="currentColor"/><circle cx="8" cy="24" r="3" fill="currentColor"/><circle cx="40" cy="24" r="3" fill="currentColor"/></svg>`,
  datafactory: `<svg viewBox="0 0 48 48" fill="none"><path d="M24 8v8M24 32v8M8 24h8M32 24h8" stroke="currentColor" stroke-width="2"/><circle cx="24" cy="24" r="8" fill="currentColor"/><circle cx="24" cy="8" r="3" fill="currentColor"/><circle cx="24" cy="40" r="3" fill="currentColor"/><circle cx="8" cy="24" r="3" fill="currentColor"/><circle cx="40" cy="24" r="3" fill="currentColor"/></svg>`,
};

// Get icon SVG string
const getServiceIcon = (serviceType) => {
  const normalized = serviceType?.toLowerCase().replace(/[\s-_\/]/g, '') || 'ec2';
  return ServiceIcons[normalized] || ServiceIcons.ec2;
};

// Service Node Component with Handles
const ServiceNode = memo(({ data, selected }) => {
  const platform = data.platform || 'AWS';
  const cloudColors = getCloudColors(platform);
  const colors = cloudColors[data.category] || cloudColors.compute;
  const iconSvg = getServiceIcon(data.serviceType);
  
  return (
    <div 
      className={`
        relative flex flex-col items-center p-2 rounded-lg transition-all duration-200
        bg-white shadow-lg border-2
        ${selected ? 'ring-2 ring-[#00f0ff] ring-offset-2 ring-offset-[#09090b]' : ''}
        hover:shadow-xl hover:scale-105
      `}
      style={{ 
        borderColor: colors.bg,
        minWidth: data.compact ? 80 : 100,
      }}
    >
      {/* Top handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-zinc-400 !border-white"
      />
      
      {/* Icon */}
      <div 
        className="w-10 h-10 mb-1 flex items-center justify-center"
        style={{ color: colors.bg }}
        dangerouslySetInnerHTML={{ __html: iconSvg }}
      />
      
      {/* Label */}
      <div className="text-center">
        <div 
          className="text-[10px] font-semibold leading-tight max-w-[90px]"
          style={{ color: colors.bg }}
        >
          {data.label}
        </div>
        {data.sublabel && (
          <div className="text-[8px] text-zinc-500 mt-0.5 max-w-[90px] truncate">
            {data.sublabel}
          </div>
        )}
      </div>
      
      {/* Cost badge */}
      {data.cost && (
        <div 
          className="absolute -top-2 -right-2 text-[8px] font-bold px-1.5 py-0.5 rounded-full text-white"
          style={{ backgroundColor: colors.bg }}
        >
          {data.cost}
        </div>
      )}
      
      {/* Bottom handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2 !h-2 !bg-zinc-400 !border-white"
      />
      
      {/* Left/Right handles for horizontal connections */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!w-2 !h-2 !bg-zinc-400 !border-white"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-2 !h-2 !bg-zinc-400 !border-white"
      />
    </div>
  );
});

ServiceNode.displayName = 'ServiceNode';

// Group/Container Node (VPC, Subnet, AZ) - Multi-cloud aware
const GroupNode = memo(({ data }) => {
  const platform = data.platform || 'AWS';
  
  // Platform-specific naming
  const platformLabels = {
    AWS: { vpc: 'VPC', region: 'AWS Region', publicSubnet: 'Public Subnet', privateSubnet: 'Private Subnet' },
    Azure: { vpc: 'VNet', region: 'Azure Region', publicSubnet: 'Public Subnet', privateSubnet: 'Private Subnet' },
    GCP: { vpc: 'VPC Network', region: 'GCP Region', publicSubnet: 'Public Subnet', privateSubnet: 'Private Subnet' },
  };
  const labels = platformLabels[platform] || platformLabels.AWS;
  
  // Platform-specific colors
  const platformColors = {
    AWS: { vpc: '#8C4FFF', region: '#232F3E' },
    Azure: { vpc: '#0078D4', region: '#3C3C41' },
    GCP: { vpc: '#4285F4', region: '#5F6368' },
  };
  const colors = platformColors[platform] || platformColors.AWS;
  
  const groupStyles = {
    vpc: { 
      border: colors.vpc, 
      bg: `${colors.vpc}0d`, 
      label: labels.vpc,
      icon: `<svg viewBox="0 0 24 24" fill="none" stroke="${colors.vpc}" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2" stroke-dasharray="3 2"/></svg>`
    },
    'public-subnet': { 
      border: '#3F8624', 
      bg: 'rgba(63, 134, 36, 0.08)', 
      label: labels.publicSubnet,
      icon: `<svg viewBox="0 0 24 24" fill="#3F8624"><circle cx="12" cy="12" r="8" opacity="0.3"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="#3F8624" stroke-width="2"/></svg>`
    },
    'private-subnet': { 
      border: '#3B48CC', 
      bg: 'rgba(59, 72, 204, 0.08)', 
      label: labels.privateSubnet,
      icon: `<svg viewBox="0 0 24 24" fill="none"><rect x="4" y="4" width="16" height="16" rx="2" fill="#3B48CC" opacity="0.3"/><path d="M12 8v8M8 12h8" stroke="#3B48CC" stroke-width="2"/></svg>`
    },
    az: { 
      border: '#FF9900', 
      bg: 'rgba(255, 153, 0, 0.05)', 
      label: 'Availability Zone',
      icon: `<svg viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="2" stroke="#FF9900" stroke-width="1.5" stroke-dasharray="4 2"/></svg>`
    },
    region: { 
      border: colors.region, 
      bg: `${colors.region}0d`, 
      label: labels.region,
      icon: null
    },
  };
  
  const style = groupStyles[data.groupType] || groupStyles.vpc;
  
  return (
    <div 
      className="rounded-lg transition-all"
      style={{ 
        width: data.width || 400, 
        height: data.height || 300,
        border: `2px dashed ${style.border}`,
        backgroundColor: style.bg,
      }}
    >
      {/* Group Label */}
      <div 
        className="absolute -top-3 left-4 px-2 flex items-center gap-1.5"
        style={{ backgroundColor: '#09090b' }}
      >
        {style.icon && (
          <div 
            className="w-4 h-4"
            dangerouslySetInnerHTML={{ __html: style.icon }}
          />
        )}
        <span 
          className="text-xs font-semibold"
          style={{ color: style.border }}
        >
          {data.label || style.label}
        </span>
      </div>
    </div>
  );
});

GroupNode.displayName = 'GroupNode';

// Node types registry
const nodeTypes = {
  service: ServiceNode,
  group: GroupNode,
};

// Parse architecture data into optimized React Flow format
const generateArchitectureLayout = (architectureData, cloudPlatform = 'AWS') => {
  const nodes = [];
  const edges = [];
  const platform = cloudPlatform?.toLowerCase() || 'aws';
  
  // Check if we have component specs
  const components = architectureData?.component_specs || [];
  const hasComponents = components.length > 0;
  
  // If no components, generate a smart default based on phase1 data
  if (!hasComponents) {
    return generateDefaultArchitecture(architectureData, platform);
  }
  
  // Parse components into categorized groups
  const categorized = categorizeComponents(components);
  
  // Generate layout with VPC and subnet structure
  return generateStructuredLayout(categorized, platform);
};

// Categorize components by type
const categorizeComponents = (components) => {
  const categories = {
    external: [],
    network: [],
    security: [],
    compute: [],
    database: [],
    storage: [],
    cache: [],
    monitoring: [],
    integration: [],
    devops: [],
  };
  
  components.forEach(comp => {
    const name = (comp.name || '').toLowerCase();
    const service = (comp.service || '').toLowerCase();
    const combined = `${name} ${service}`;
    
    if (/user|client|browser|mobile|internet/i.test(combined)) {
      categories.external.push(comp);
    } else if (/cdn|cloudfront|route53|dns|elb|alb|nlb|load.?balancer|api.?gateway|nat|vpc/i.test(combined)) {
      categories.network.push(comp);
    } else if (/waf|firewall|shield|iam|kms|secret|acm|cognito/i.test(combined)) {
      categories.security.push(comp);
    } else if (/ec2|ecs|eks|fargate|lambda|compute|server|container|kubernetes|batch/i.test(combined)) {
      categories.compute.push(comp);
    } else if (/rds|aurora|dynamodb|database|postgres|mysql|documentdb|neptune/i.test(combined)) {
      categories.database.push(comp);
    } else if (/s3|storage|bucket|ebs|efs|glacier/i.test(combined)) {
      categories.storage.push(comp);
    } else if (/redis|elasticache|memcached|cache/i.test(combined)) {
      categories.cache.push(comp);
    } else if (/cloudwatch|monitor|log|metric|xray|trace/i.test(combined)) {
      categories.monitoring.push(comp);
    } else if (/sns|sqs|kinesis|eventbridge|step.?function|mq/i.test(combined)) {
      categories.integration.push(comp);
    } else if (/codepipeline|codebuild|codecommit|codedeploy|ecr|jenkins/i.test(combined)) {
      categories.devops.push(comp);
    } else {
      categories.compute.push(comp);
    }
  });
  
  return categories;
};

// Generate structured layout with VPC boundaries
const generateStructuredLayout = (categorized, platform) => {
  const nodes = [];
  const edges = [];
  
  const SPACING = { x: 160, y: 140 };
  const VPC_PADDING = 40;
  let nodeId = 0;
  const normalizedPlatform = platform?.charAt(0).toUpperCase() + platform?.slice(1).toLowerCase() || 'AWS';
  
  // Platform-specific VPC naming
  const vpcLabels = {
    AWS: 'VPC (10.0.0.0/16)',
    Azure: 'VNet (10.0.0.0/16)',
    GCP: 'VPC Network (10.0.0.0/16)',
  };
  
  // Calculate layout dimensions based on content
  const computeCount = categorized.compute.length || 2;
  const dbCount = categorized.database.length + categorized.cache.length || 1;
  
  const vpcWidth = Math.max(600, (computeCount + 1) * SPACING.x + VPC_PADDING * 2);
  const vpcHeight = 400;
  
  // Add Region container (largest)
  nodes.push({
    id: 'region',
    type: 'group',
    position: { x: 50, y: 80 },
    data: { groupType: 'region', label: `${normalizedPlatform} Region`, platform: normalizedPlatform, width: vpcWidth + 100, height: vpcHeight + 200 },
    style: { zIndex: -3 },
    draggable: false,
  });
  
  // Add VPC container
  nodes.push({
    id: 'vpc',
    type: 'group',
    position: { x: 100, y: 180 },
    data: { groupType: 'vpc', label: vpcLabels[normalizedPlatform] || vpcLabels.AWS, platform: normalizedPlatform, width: vpcWidth, height: vpcHeight },
    style: { zIndex: -2 },
    draggable: false,
  });
  
  // Add Public Subnet
  nodes.push({
    id: 'public-subnet',
    type: 'group',
    position: { x: 120, y: 220 },
    data: { groupType: 'public-subnet', label: 'Public Subnet (10.0.1.0/24)', platform: normalizedPlatform, width: vpcWidth - 40, height: 140 },
    data: { groupType: 'public-subnet', label: 'Public Subnet (10.0.1.0/24)', width: vpcWidth - 40, height: 140 },
    style: { zIndex: -1 },
    draggable: false,
  });
  
  // Add Private Subnet
  nodes.push({
    id: 'private-subnet',
    type: 'group',
    position: { x: 120, y: 380 },
    data: { groupType: 'private-subnet', label: 'Private Subnet (10.0.2.0/24)', width: vpcWidth - 40, height: 180 },
    style: { zIndex: -1 },
    draggable: false,
  });
  
  // External users/clients (outside VPC)
  const userNodeId = `node-${nodeId++}`;
  nodes.push({
    id: userNodeId,
    type: 'service',
    position: { x: vpcWidth / 2 + 50, y: 20 },
    data: { 
      label: 'Users', 
      serviceType: 'users', 
      category: 'external',
    },
  });
  
  // CDN/Edge (if present, outside VPC)
  let lastExternalId = userNodeId;
  const cdnComp = categorized.network.find(c => /cloudfront|cdn/i.test(`${c.name} ${c.service}`));
  if (cdnComp) {
    const cdnId = `node-${nodeId++}`;
    nodes.push({
      id: cdnId,
      type: 'service',
      position: { x: vpcWidth / 2 + 50, y: 100 },
      data: { 
        label: cdnComp.name || 'CloudFront', 
        sublabel: cdnComp.service,
        serviceType: 'cloudfront', 
        category: 'network',
        cost: cdnComp.monthly_cost,
      },
    });
    edges.push({ id: `e-${userNodeId}-${cdnId}`, source: userNodeId, target: cdnId, animated: true });
    lastExternalId = cdnId;
  }
  
  // Network layer in public subnet (ALB, WAF, API Gateway)
  let publicSubnetX = 180;
  let lastNetworkId = lastExternalId;
  
  // WAF (if present)
  const wafComp = categorized.security.find(c => /waf|firewall/i.test(`${c.name} ${c.service}`));
  if (wafComp) {
    const wafId = `node-${nodeId++}`;
    nodes.push({
      id: wafId,
      type: 'service',
      position: { x: publicSubnetX, y: 250 },
      data: { 
        label: wafComp.name || 'WAF', 
        serviceType: 'waf', 
        category: 'security',
        cost: wafComp.monthly_cost,
      },
    });
    edges.push({ id: `e-${lastNetworkId}-${wafId}`, source: lastNetworkId, target: wafId });
    lastNetworkId = wafId;
    publicSubnetX += SPACING.x;
  }
  
  // Load Balancer
  const lbComp = categorized.network.find(c => /elb|alb|nlb|load.?balancer/i.test(`${c.name} ${c.service}`));
  const lbId = `node-${nodeId++}`;
  nodes.push({
    id: lbId,
    type: 'service',
    position: { x: publicSubnetX, y: 250 },
    data: { 
      label: lbComp?.name || 'Application Load Balancer', 
      sublabel: lbComp?.service || 'ALB',
      serviceType: 'elb', 
      category: 'network',
      cost: lbComp?.monthly_cost,
    },
  });
  edges.push({ id: `e-${lastNetworkId}-${lbId}`, source: lastNetworkId, target: lbId });
  publicSubnetX += SPACING.x;
  
  // NAT Gateway (in public subnet)
  const natId = `node-${nodeId++}`;
  nodes.push({
    id: natId,
    type: 'service',
    position: { x: publicSubnetX + 100, y: 250 },
    data: { 
      label: 'NAT Gateway', 
      serviceType: 'nat', 
      category: 'network',
    },
  });
  
  // Compute layer in private subnet
  let privateSubnetX = 180;
  const computeNodes = [];
  
  categorized.compute.forEach((comp, idx) => {
    const compId = `node-${nodeId++}`;
    nodes.push({
      id: compId,
      type: 'service',
      position: { x: privateSubnetX + idx * SPACING.x, y: 420 },
      data: { 
        label: comp.name || `App Server ${idx + 1}`, 
        sublabel: comp.service,
        serviceType: getServiceType(comp), 
        category: 'compute',
        cost: comp.monthly_cost,
      },
    });
    computeNodes.push(compId);
    edges.push({ id: `e-${lbId}-${compId}`, source: lbId, target: compId });
  });
  
  // If no compute specified, add default
  if (computeNodes.length === 0) {
    for (let i = 0; i < 2; i++) {
      const compId = `node-${nodeId++}`;
      nodes.push({
        id: compId,
        type: 'service',
        position: { x: privateSubnetX + i * SPACING.x, y: 420 },
        data: { 
          label: `App Server ${i + 1}`, 
          serviceType: 'ecs', 
          category: 'compute',
        },
      });
      computeNodes.push(compId);
      edges.push({ id: `e-${lbId}-${compId}`, source: lbId, target: compId });
    }
  }
  
  // Database layer
  let dbX = privateSubnetX + computeNodes.length * SPACING.x + 80;
  
  categorized.database.forEach((comp, idx) => {
    const dbId = `node-${nodeId++}`;
    nodes.push({
      id: dbId,
      type: 'service',
      position: { x: dbX, y: 420 + idx * 80 },
      data: { 
        label: comp.name || 'Database', 
        sublabel: comp.service,
        serviceType: getServiceType(comp), 
        category: 'database',
        cost: comp.monthly_cost,
      },
    });
    // Connect all compute to database
    computeNodes.forEach(compId => {
      edges.push({ id: `e-${compId}-${dbId}`, source: compId, target: dbId, sourceHandle: 'right', targetHandle: 'left' });
    });
  });
  
  // Cache (if present)
  categorized.cache.forEach((comp, idx) => {
    const cacheId = `node-${nodeId++}`;
    nodes.push({
      id: cacheId,
      type: 'service',
      position: { x: dbX + 140, y: 420 + idx * 80 },
      data: { 
        label: comp.name || 'Cache', 
        sublabel: comp.service,
        serviceType: 'elasticache', 
        category: 'database',
        cost: comp.monthly_cost,
      },
    });
    computeNodes.forEach(compId => {
      edges.push({ id: `e-${compId}-${cacheId}`, source: compId, target: cacheId, sourceHandle: 'right' });
    });
  });
  
  // Storage (S3 - outside VPC but connected)
  categorized.storage.forEach((comp, idx) => {
    const storageId = `node-${nodeId++}`;
    nodes.push({
      id: storageId,
      type: 'service',
      position: { x: vpcWidth + 120, y: 300 + idx * 80 },
      data: { 
        label: comp.name || 'S3 Bucket', 
        sublabel: comp.service,
        serviceType: 's3', 
        category: 'storage',
        cost: comp.monthly_cost,
      },
    });
    // Connect compute to storage
    if (computeNodes.length > 0) {
      edges.push({ id: `e-${computeNodes[0]}-${storageId}`, source: computeNodes[0], target: storageId, sourceHandle: 'right' });
    }
  });
  
  // Monitoring (outside VPC)
  categorized.monitoring.forEach((comp, idx) => {
    const monId = `node-${nodeId++}`;
    nodes.push({
      id: monId,
      type: 'service',
      position: { x: vpcWidth + 120, y: 450 + idx * 70 },
      data: { 
        label: comp.name || 'CloudWatch', 
        sublabel: comp.service,
        serviceType: 'cloudwatch', 
        category: 'management',
        cost: comp.monthly_cost,
      },
    });
  });
  
  // Apply edge styling
  const styledEdges = edges.map(e => ({
    ...e,
    style: { stroke: '#94a3b8', strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 15, height: 15 },
    type: 'smoothstep',
  }));
  
  return { nodes, edges: styledEdges };
};

// Get service type from component
const getServiceType = (comp) => {
  const combined = `${comp.name || ''} ${comp.service || ''}`.toLowerCase();
  
  if (/aurora/i.test(combined)) return 'aurora';
  if (/dynamodb/i.test(combined)) return 'dynamodb';
  if (/rds|postgres|mysql/i.test(combined)) return 'rds';
  if (/elasticache|redis/i.test(combined)) return 'elasticache';
  if (/lambda/i.test(combined)) return 'lambda';
  if (/fargate/i.test(combined)) return 'fargate';
  if (/eks|kubernetes/i.test(combined)) return 'eks';
  if (/ecs|container/i.test(combined)) return 'ecs';
  if (/ec2/i.test(combined)) return 'ec2';
  if (/s3|bucket/i.test(combined)) return 's3';
  if (/cloudfront|cdn/i.test(combined)) return 'cloudfront';
  if (/elb|alb|load/i.test(combined)) return 'elb';
  if (/waf/i.test(combined)) return 'waf';
  if (/api.?gateway/i.test(combined)) return 'apigateway';
  if (/sns/i.test(combined)) return 'sns';
  if (/sqs/i.test(combined)) return 'sqs';
  if (/kinesis/i.test(combined)) return 'kinesis';
  if (/cloudwatch/i.test(combined)) return 'cloudwatch';
  if (/secret/i.test(combined)) return 'secretsmanager';
  if (/kms/i.test(combined)) return 'kms';
  if (/iam/i.test(combined)) return 'iam';
  
  return 'ec2';
};

// Generate default architecture when no components provided
const generateDefaultArchitecture = (architectureData, platform) => {
  const nodes = [];
  const edges = [];
  
  // Create a sensible default based on typical 3-tier architecture
  const defaultComponents = {
    external: [{ name: 'Users', service: '' }],
    network: [
      { name: 'CloudFront', service: 'CDN' },
      { name: 'Application Load Balancer', service: 'ALB' },
    ],
    security: [{ name: 'AWS WAF', service: 'Web Application Firewall' }],
    compute: [
      { name: 'App Server 1', service: 'ECS Fargate' },
      { name: 'App Server 2', service: 'ECS Fargate' },
    ],
    database: [{ name: 'Primary Database', service: 'RDS PostgreSQL' }],
    cache: [{ name: 'Cache Cluster', service: 'ElastiCache Redis' }],
    storage: [{ name: 'Static Assets', service: 'S3' }],
    monitoring: [{ name: 'Monitoring', service: 'CloudWatch' }],
    integration: [],
    devops: [],
  };
  
  return generateStructuredLayout(defaultComponents, platform);
};

// Main Component
export const EnterpriseArchitectureDiagram = memo(({ 
  architectureData, 
  cloudPlatform = "AWS",
  className = "",
  onExport,
}) => {
  const [viewMode, setViewMode] = useState("visual");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => generateArchitectureLayout(architectureData, cloudPlatform),
    [architectureData, cloudPlatform]
  );
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  
  // Update when data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = generateArchitectureLayout(architectureData, cloudPlatform);
    setNodes(newNodes);
    setEdges(newEdges);
  }, [architectureData, cloudPlatform, setNodes, setEdges]);
  
  // Export diagram as PNG
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const element = document.querySelector('[data-testid="architecture-flow-container"]');
      if (element) {
        const canvas = await html2canvas(element, {
          backgroundColor: '#09090b',
          scale: 2,
          useCORS: true,
        });
        const link = document.createElement('a');
        link.download = `${cloudPlatform}_Architecture_Diagram.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (err) {
      console.error('Export failed:', err);
    }
    setIsExporting(false);
  }, [cloudPlatform]);
  
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);
  
  const diagramCode = architectureData?.architecture_diagram || 'No architecture code available';

  return (
    <Card 
      className={`bg-[#09090b] border-white/10 overflow-hidden ${isFullscreen ? 'fixed inset-4 z-50' : ''} ${className}`}
      data-testid="enterprise-architecture-diagram"
    >
      <CardHeader className="pb-2 border-b border-white/5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Box className="w-5 h-5 text-[#00f0ff]" />
            Technical Architecture
            <span className="text-xs text-zinc-500 font-normal ml-2 px-2 py-0.5 bg-zinc-800 rounded">
              {cloudPlatform}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="bg-zinc-900 border border-white/10 h-8">
                <TabsTrigger 
                  value="visual" 
                  className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs h-6 px-3 gap-1"
                >
                  <Eye className="w-3 h-3" />
                  Visual
                </TabsTrigger>
                <TabsTrigger 
                  value="code" 
                  className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black text-xs h-6 px-3 gap-1"
                >
                  <Code className="w-3 h-3" />
                  Code
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              className="text-zinc-400 hover:text-white h-8"
              title="Export as PNG"
            >
              <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-zinc-400 hover:text-white h-8"
              title="Toggle fullscreen"
            >
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {viewMode === "visual" ? (
          <div 
            data-testid="architecture-flow-container"
            className="bg-[#0a0a0b]"
            style={{ height: isFullscreen ? 'calc(100vh - 140px)' : '600px' }}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15, maxZoom: 1.2 }}
              minZoom={0.3}
              maxZoom={2}
              defaultEdgeOptions={{
                type: 'smoothstep',
                style: { stroke: '#94a3b8', strokeWidth: 1.5 },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
              }}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#1f1f23" gap={20} size={1} />
              <Controls 
                className="!bg-zinc-900 !border-white/10 !rounded-lg [&>button]:!bg-zinc-800 [&>button]:!border-white/10 [&>button:hover]:!bg-zinc-700 [&>button]:!text-white"
                showInteractive={false}
              />
              <MiniMap 
                className="!bg-zinc-900/90 !border-white/10 !rounded-lg"
                nodeColor={(node) => {
                  if (node.type === 'group') return 'transparent';
                  const cat = node.data?.category;
                  const platform = node.data?.platform || 'AWS';
                  const cloudColors = getCloudColors(platform);
                  return cloudColors[cat]?.bg || '#6B7280';
                }}
                maskColor="rgba(0,0,0,0.85)"
                pannable
                zoomable
              />
              {/* Legend */}
              <Panel position="bottom-left" className="!m-3">
                <div className="bg-zinc-900/95 backdrop-blur border border-white/10 rounded-lg p-3">
                  <div className="text-[10px] text-zinc-400 font-semibold mb-2 uppercase tracking-wide">Legend</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[10px]">
                    {Object.entries(getCloudColors(cloudPlatform)).slice(0, 6).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: val.bg }} />
                        <span className="text-zinc-300 capitalize">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            </ReactFlow>
          </div>
        ) : (
          <div className="bg-zinc-950 p-4 max-h-[600px] overflow-auto">
            <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
              {diagramCode}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

EnterpriseArchitectureDiagram.displayName = 'EnterpriseArchitectureDiagram';

export default EnterpriseArchitectureDiagram;
